import { Loader, TextInput } from '@mantine/core';
import { getExceptionDetails, Icon, variants } from '@uag/react-core';
import { clsx } from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { OnboardingRequestDetailModel } from 'api/models';
import { useCompanyExistsHook } from 'api/queries/companies/companies';
import { useGetRequestsHook } from 'api/queries/requests/requests';

type Props = {
    onCustomerNumberChanged: (value: string, isCountrySupportedInDoka360: boolean) => void;
    onRequestAlreadyExists?: (existingRequest: OnboardingRequestDetailModel) => void;
    initialCustomerNumber?: string;
    showDescription?: boolean;
    fullWidth?: boolean;
    requireDoka360Support?: boolean;
};

export const CustomerNumberField = ({
    onCustomerNumberChanged: handleCustomerNumberChange,
    onRequestAlreadyExists: handleRequestAlreadyExists,
    initialCustomerNumber = '',
    showDescription = true,
    fullWidth = false,
    requireDoka360Support = false,
}: Props) => {
    const { t } = useTranslation();
    const [isCustomerNumberValidationPending, setIsCustomerNumberValidationPending] = useState(false);
    const [customerNumberError, setCustomerNumberError] = useState<string | undefined>();
    const [customerNumber, setCustomerNumber] = useState<string>(initialCustomerNumber);
    const [isCustomerNumberValid, setIsCustomerNumberValid] = useState<boolean | undefined>();
    const getOnboardingRequests = useGetRequestsHook();
    const getCompanyExists = useCompanyExistsHook();

    const hasValidatedInitialCustomerNumber = useRef(false);
    const handleCustomerNumberBlur = useCallback(
        async (value: string) => {
            handleCustomerNumberChange('', false);
            setCustomerNumberError(undefined);
            setIsCustomerNumberValid(undefined);

            if (!value) {
                return;
            }

            setIsCustomerNumberValidationPending(true);

            try {
                if (handleRequestAlreadyExists) {
                    const existingRequests = await getOnboardingRequests();
                    const existingRequest = existingRequests.data.find(
                        (response) => response.companyData.customerIdentifier === value,
                    );

                    if (existingRequest) {
                        handleRequestAlreadyExists(existingRequest);
                    }
                }

                const response = await getCompanyExists(value);

                if (response.data?.exists) {
                    if (requireDoka360Support && !response.data.isCountrySupportedInDoka360) {
                        setCustomerNumberError(t('customerNumberNotSupportedForDoka360'));
                        setIsCustomerNumberValid(false);
                    } else {
                        setIsCustomerNumberValid(true);
                    }

                    handleCustomerNumberChange(value, response.data.isCountrySupportedInDoka360);
                } else {
                    setCustomerNumberError(t('customerNotFound'));
                    setIsCustomerNumberValid(false);
                }
            } catch (error) {
                setIsCustomerNumberValid(false);
                setCustomerNumberError(getExceptionDetails(error)?.message);
            } finally {
                setIsCustomerNumberValidationPending(false);
            }
        },
        [
            handleCustomerNumberChange,
            handleRequestAlreadyExists,
            getCompanyExists,
            getOnboardingRequests,
            requireDoka360Support,
            t,
        ],
    );

    useEffect(() => {
        if (hasValidatedInitialCustomerNumber.current || !initialCustomerNumber) {
            return;
        }

        hasValidatedInitialCustomerNumber.current = true;
        void handleCustomerNumberBlur(initialCustomerNumber);
    }, [handleCustomerNumberBlur, initialCustomerNumber]);

    const rightSection = isCustomerNumberValidationPending ? (
        <Loader />
    ) : isCustomerNumberValid === true ? (
        <Icon color="green" icon="check" />
    ) : isCustomerNumberValid === false ? (
        <Icon color="red" icon="close" />
    ) : (
        <Icon icon="circle" />
    );

    return (
        <TextInput
            classNames={{ root: clsx(fullWidth && 'w-full'), section: 'border-l-0' }}
            description={showDescription ? t('customerNumberDescription') : undefined}
            error={customerNumberError}
            label={t('customerNumber')}
            placeholder={t('customerNumberPlaceholder')}
            rightSection={rightSection}
            value={customerNumber}
            variant={variants.textField.textDecoration}
            required
            onBlur={(event) => handleCustomerNumberBlur(event.target.value)}
            onChange={(event) => setCustomerNumber(event.target.value)}
        />
    );
};
